import { supabase } from './supabaseClient';

const DEBUG = false; // set true to see console logs during troubleshooting

// ========== INDIA CITIES CACHING (Reduce Bandwidth) ==========
const INDIA_CITIES_CACHE_KEY = 'india_cities_cache';
const CACHE_VERSION = 'v5'; // Bumped to refresh cache: ascii_name unaccent cleanup (Apr 2026)
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

// IndiaCityRow type defined later in file (line ~652)
type CachedIndiaCityRow = {
  name: string | null;
  ascii_name: string | null;
  state: string | null;
  country: string | null;
  lat: number | null;
  lon: number | null;
  timezone: string | null;
};

interface CachedIndiaCities {
  version: string;
  timestamp: number;
  data: CachedIndiaCityRow[];
}

let inMemoryCache: CachedIndiaCityRow[] | null = null;

async function getIndiaCitiesFromCache(): Promise<CachedIndiaCityRow[] | null> {
  // Check in-memory cache first (fastest)
  if (inMemoryCache) return inMemoryCache;

  try {
    const cached = localStorage.getItem(INDIA_CITIES_CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedIndiaCities = JSON.parse(cached);
    const isExpired = Date.now() - parsed.timestamp > CACHE_DURATION;
    
    if (parsed.version !== CACHE_VERSION || isExpired) {
      localStorage.removeItem(INDIA_CITIES_CACHE_KEY);
      return null;
    }
    
    // Populate in-memory cache
    inMemoryCache = parsed.data;
    if (DEBUG) console.log(`[cityUtils] Loaded ${inMemoryCache.length} cities from localStorage cache`);
    return inMemoryCache;
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] Failed to read cache:', err);
    return null;
  }
}

function saveIndiaCitiesToCache(data: CachedIndiaCityRow[]) {
  try {
    const cached: CachedIndiaCities = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      data
    };
    const jsonStr = JSON.stringify(cached);
    const sizeMB = (jsonStr.length / 1024 / 1024).toFixed(2);
    
    localStorage.setItem(INDIA_CITIES_CACHE_KEY, jsonStr);
    inMemoryCache = data; // Also cache in memory
    if (DEBUG) console.log(`[cityUtils] Saved ${data.length} cities to localStorage cache (${sizeMB} MB)`);
  } catch (err) {
    console.error('[cityUtils] Failed to save cities to cache (localStorage full?):', err);
    // Cache in memory only as fallback
    inMemoryCache = data;
    if (DEBUG) console.log(`[cityUtils] Using in-memory cache only (${data.length} cities)`);
  }
}

export type CityOption = {
  label: string;                 // "City, Region, Country"
  value: string;                 // stable id for react-select
  city?: string;
  region?: string;
  country?: string;
  lat: number;
  lon: number;                   // keep lon for internal use
  lng: number;                   // also expose lng for UI code that expects it
  tz: string;                    // IANA tz, e.g. "Asia/Kolkata"
  timezone: string;              // duplicate for compatibility
  source: 'opencage' | 'edge' | 'nominatim' | 'static' | 'db';
};

/* ---------- ENV: tolerate Vite/Next/CRA/global ---------- */
function readOCKey(): string {
  // CRA injects at build time via webpack DefinePlugin
  const key = process.env.REACT_APP_OPENCAGE_KEY || '';
  
  if (DEBUG && key) console.log('[cityUtils] OpenCage API key loaded:', key.substring(0, 10) + '...');
  if (DEBUG && !key) console.error('[cityUtils] CRITICAL: OpenCage API key NOT found in process.env');
  
  return key;
}
const OC_KEY = readOCKey();

/** A tiny set to show something before user types */
const POPULAR_CITY_OPTIONS_RAW = [
  { label: 'Mumbai, IN',     city: 'Mumbai',     country: 'India',           lat: 19.0760, lon: 72.8777,  tz: 'Asia/Kolkata',      source: 'static' as const },
  { label: 'Delhi, IN',      city: 'Delhi',      country: 'India',           lat: 28.6139, lon: 77.2090,  tz: 'Asia/Kolkata',      source: 'static' as const },
  { label: 'Bengaluru, IN',  city: 'Bengaluru',  country: 'India',           lat: 12.9716, lon: 77.5946,  tz: 'Asia/Kolkata',      source: 'static' as const },
  { label: 'Pune, IN',       city: 'Pune',       country: 'India',           lat: 18.5204, lon: 73.8567,  tz: 'Asia/Kolkata',      source: 'static' as const },
  { label: 'Jamnagar, IN',   city: 'Jamnagar',   country: 'India',           lat: 22.4707, lon: 70.0577,  tz: 'Asia/Kolkata',      source: 'static' as const },
  { label: 'Panvel, IN',     city: 'Panvel',     country: 'India',           lat: 18.9894, lon: 73.1175,  tz: 'Asia/Kolkata',      source: 'static' as const },
  { label: 'New York, US',   city: 'New York',   country: 'United States',   lat: 40.7128, lon: -74.0060, tz: 'America/New_York',  source: 'static' as const },
  { label: 'London, GB',     city: 'London',     country: 'United Kingdom',  lat: 51.5074, lon: -0.1278,  tz: 'Europe/London',     source: 'static' as const },
];

/* ---------- helpers ---------- */
const CITYLIKE_KEYS = [
  'city','town','village','municipality','hamlet','locality','suburb','state_district'
] as const;

function pickCityLike(comp: any): string | undefined {
  for (const k of CITYLIKE_KEYS) {
    if (comp?.[k]) return comp[k];
  }
  return undefined;
}

function pickRegion(comp: any): string | undefined {
  return comp?.state || comp?.province || comp?.region || comp?.county || comp?.state_district;
}

function cleanCityName(...candidates: Array<any>): string | undefined {
  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    if (!/[A-Za-z]/.test(trimmed)) continue;  // skip pure numeric codes
    return trimmed.replace(/\s+/g, ' ');
  }
  return undefined;
}

// Indian state codes to names mapping (GeoNames state codes from india_cities.csv)
// Verified with major cities: Mumbai=16, Delhi=07, Kolkata=28, Bengaluru=19, Chennai=25, Kochi=17
const INDIA_STATE_CODES: Record<string, string> = {
  '01': 'Andaman and Nicobar',
  '02': 'Andhra Pradesh',
  '03': 'Arunachal Pradesh',
  '04': 'Punjab',
  '05': 'Bihar',
  '06': 'Chandigarh',
  '07': 'Delhi',
  '08': 'Dadra and Nagar Haveli',
  '09': 'Gujarat',
  '10': 'Haryana',
  '11': 'Himachal Pradesh',
  '12': 'Jammu and Kashmir',
  '13': 'Karnataka',
  '14': 'Lakshadweep',
  '15': 'Lakshadweep',
  '16': 'Maharashtra',
  '17': 'Kerala',  // FIXED: Was 'Manipur', but state code 17 is Kerala (Kochi, Thiruvananthapuram, etc.)
  '18': 'Meghalaya',
  '19': 'Karnataka',
  '20': 'Nagaland',
  '21': 'Odisha',
  '22': 'Puducherry',
  '23': 'Punjab',
  '24': 'Rajasthan',
  '25': 'Tamil Nadu',
  '26': 'Tripura',
  '27': 'Uttarakhand',
  '28': 'West Bengal',
  '29': 'Sikkim',
  '30': 'Assam',
  '31': 'Telangana',
  '32': 'Kerala',  // FIXED: Was 'Andhra Pradesh', but 32 is also Kerala in some data sources
  '33': 'Goa',
  '34': 'Chhattisgarh',
  '35': 'Madhya Pradesh',
  '36': 'Uttar Pradesh',
  '37': 'Chhattisgarh',
  '38': 'Jharkhand',
  '39': 'Uttarakhand',
  '40': 'Telangana',
  '41': 'Ladakh',
  '42': 'Manipur',  // ADDED: Manipur was missing
  '52': 'Dadra and Nagar Haveli and Daman and Diu',
};

function cleanStateName(raw: any): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  
  // Check if it's a numeric state code (GeoNames format)
  if (/^[0-9]+$/.test(trimmed)) {
    const stateCode = trimmed.padStart(2, '0');
    return INDIA_STATE_CODES[stateCode] || undefined;
  }
  
  const normalized = trimmed.replace(/\s+/g, ' ');
  const upper = normalized.toUpperCase();
  if (/^[A-Z]{2}[.-][0-9]+$/.test(upper)) return undefined;  // e.g. IN.09
  if (/^IN[.-][0-9]+$/.test(upper)) return undefined;
  return normalized;
}

function safeNumber(n: any): number | null {
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}

function makeValue(lat: number, lon: number, tz: string) {
  return `${lat.toFixed(5)},${lon.toFixed(5)}|${tz}`;
}

function hydrateOption(partial: {
  label: string;
  city?: string;
  region?: string;
  country?: string;
  lat: number;
  lon: number;
  tz: string;
  source: CityOption['source'];
}): CityOption {
  const { label, city, region, country, lat, lon, tz, source } = partial;
  return {
    label,
    value: makeValue(lat, lon, tz || 'UTC'),
    city,
    region,
    country,
    lat,
    lon,
    lng: lon,
    tz: tz || 'UTC',
    timezone: tz || 'UTC',
    source,
  };
}

export const POPULAR_CITY_OPTIONS: CityOption[] =
  POPULAR_CITY_OPTIONS_RAW.map(o => hydrateOption(o));

const OC_TYPES = 'city,town,municipality,village';
const COUNTRY_PRIORITY_CODES = 'in,pk,bd,np,lk,ae,sa,qa,kw,bh,om,eg,za,ng,ke,gh,et,ma,tn,dz,ly,na,bw,zm,zw,ug,tz,rw,cm,ci,jo,lb,iq,ir,ca';
const COUNTRY_PRIORITY_SET = new Set(
  COUNTRY_PRIORITY_CODES.split(',')
    .map(code => code.trim().toLowerCase())
    .filter(Boolean)
);

type OCParam = string | number | boolean | undefined;
type OCParamMap = Record<string, OCParam>;

/* ---------- Timezone helpers ---------- */
const TIMEZONE_CACHE = new Map<string, string>();

function coordKey(lat: number, lon: number): string {
  return `${lat.toFixed(4)},${lon.toFixed(4)}`;
}

const INDIA_LAT_MIN = 6;
const INDIA_LAT_MAX = 38;
const INDIA_LON_MIN = 68;
const INDIA_LON_MAX = 98;

export async function resolveTimezoneForCoords(lat: number, lon: number): Promise<string | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  const key = coordKey(lat, lon);
  const cached = TIMEZONE_CACHE.get(key);
  if (cached) return cached;

  // India uses a single time zone; avoid a needless network round trip
  if (
    lat >= INDIA_LAT_MIN && lat <= INDIA_LAT_MAX &&
    lon >= INDIA_LON_MIN && lon <= INDIA_LON_MAX
  ) {
    TIMEZONE_CACHE.set(key, 'Asia/Kolkata');
    return 'Asia/Kolkata';
  }

  if (typeof fetch !== 'function') return null;

  try {
    const url = `https://api.open-meteo.com/v1/timezone?latitude=${lat}&longitude=${lon}`;
    const resp = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!resp.ok) return null;
    const json = await resp.json();
    const tz = (json?.timezone ?? json?.timeZone) as string | undefined;
    if (typeof tz === 'string' && tz.trim()) {
      const clean = tz.trim();
      TIMEZONE_CACHE.set(key, clean);
      return clean;
    }
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] resolveTimezoneForCoords failed', err);
  }

  return null;
}

/** Normalize OpenCage result into CityOption[] and prioritize *real* cities */
// Accept all location types that OpenCage returns - if it has a city name and coordinates, it's valid for astrology purposes
const OC_ALLOWED_TYPES = new Set(['city', 'town', 'village', 'municipality', 'administrative', 'locality', 'suburb', 'neighbourhood', 'region', 'aeroway', 'shop', 'building', 'subward', 'hamlet', 'road']);
function mapOpenCage(json: any, source: CityOption['source']): CityOption[] {
  const raw: any[] = Array.isArray(json?.results) ? json.results : [];
  const seen = new Set<string>();
  const ranked: Array<{ option: CityOption; confidence: number; labelLen: number; priority: number }> = [];

  for (const entry of raw) {
    const comp = entry?.components ?? entry?.raw?.components ?? {};
    const annotations = entry?.annotations ?? entry?.raw?.annotations ?? {};
    const typeRaw = (comp?._type ?? comp?.type ?? entry?.type ?? '').toString().toLowerCase();
    const city = pickCityLike(comp);
    if (!city) continue;
    if (typeRaw && !OC_ALLOWED_TYPES.has(typeRaw)) continue;

    const region = pickRegion(comp);
    const country = comp.country || entry?.country || null;
    const lat = safeNumber(entry?.lat ?? entry?.geometry?.lat);
    const lon = safeNumber(entry?.lon ?? entry?.geometry?.lng ?? entry?.geometry?.lon);
    const tz = (entry?.tz ?? annotations?.timezone?.name ?? '').toString();
    if (lat == null || lon == null) continue;
    if (!tz) continue;

    const label = [city, region, country]
      .filter(Boolean)
      .join(', ') || entry?.label || entry?.formatted || `${lat.toFixed(3)}, ${lon.toFixed(3)}`;

    const option = hydrateOption({
      label,
      city,
      region: region ?? undefined,
      country: country ?? undefined,
      lat,
      lon,
      tz,
      source,
    });

    const key = option.value;
    if (seen.has(key)) continue;
    seen.add(key);

    const confidence = Number(entry?.confidence ?? entry?.raw?.confidence ?? 0);
    const countryCode = (comp.country_code ?? comp.ISO_3166_1_alpha_2 ?? comp['ISO_3166-1_alpha-2'] ?? '')
      .toString()
      .toLowerCase();
    const priority = countryCode && COUNTRY_PRIORITY_SET.has(countryCode) ? 0 : 1;

    ranked.push({
      option,
      confidence: Number.isFinite(confidence) ? confidence : 0,
      labelLen: option.label.length,
      priority,
    });
  }

  ranked.sort((a, b) =>
    a.priority - b.priority ||
    b.confidence - a.confidence ||
    a.labelLen - b.labelLen ||
    a.option.label.localeCompare(b.option.label)
  );

  return ranked.map(r => r.option);
}

async function searchIndiaCities(q: string): Promise<CityOption[]> {
  const normalized = normalizeQuery(q);
  if (!normalized) return [];

  const terms = normalized.split(' ').filter(Boolean);
  if (!terms.length) return [];

  try {
    // Try full-text search first (fast, indexed)
    let { data, error } = await supabase
      .from('india_cities')
      .select('name, ascii_name, state, country, lat, lon, timezone')
      .textSearch('search_tokens', normalized, { type: 'websearch' })
      .limit(100);
    
    if (DEBUG) console.log('[searchIndiaCities] Full-text search results:', data?.length, 'cities for query:', normalized);
    
    // Fallback to ILIKE prefix match if full-text returns nothing (handles partial typing)
    if (!error && (!data || data.length === 0)) {
      if (DEBUG) console.log('[searchIndiaCities] No full-text results, trying ILIKE prefix match');
      const ilikeFallback = await supabase
        .from('india_cities')
        .select('name, ascii_name, state, country, lat, lon, timezone')
        .ilike('ascii_name', `${normalized}%`)
        .limit(50);
      
      if (ilikeFallback.data && ilikeFallback.data.length > 0) {
        data = ilikeFallback.data;
        error = null;
        if (DEBUG) console.log('[searchIndiaCities] ILIKE fallback found:', data?.length, 'cities');
      }
    }
    
    if (error || !data) {
      if (DEBUG) console.warn('[searchIndiaCities] Search failed:', error);
      return [];
    }

    const rows: CachedIndiaCityRow[] = data as CachedIndiaCityRow[];
    const seen = new Set<string>();

    const ranked = rows
      .map(row => {
        if (row?.lat == null || row?.lon == null) return null;

        // Use ascii_name (name column contains IDs, not city names)
        const cityName = cleanCityName(row?.ascii_name, row?.ascii_name);
        if (!cityName) return null;

        const cityNorm = cityName.toLowerCase();
        const asciiNorm = cityNorm;
        const state = cleanStateName(row?.state);
        
        // Build label with state for disambiguation
        const labelParts = state 
          ? [cityName, state, row?.country || 'India']
          : [cityName, row?.country || 'India'];

        const option = hydrateOption({
          label: labelParts.join(', '),
          city: cityName,
          region: state || undefined,
          country: row?.country || 'India',
          lat: Number(row.lat),
          lon: Number(row.lon),
          tz: row?.timezone || 'Asia/Kolkata',
          source: 'db',
        });

        // Improved priority scoring
        let priority = 10; // Default low priority
        
        // Exact match (highest priority)
        if (cityNorm === normalized || asciiNorm === normalized) {
          priority = 0;
        }
        // Starts with query (very high priority)
        else if (cityNorm.startsWith(normalized) || asciiNorm.startsWith(normalized)) {
          priority = 1;
        }
        // Word boundary match (e.g., "navi" matches "Navi Mumbai")
        else if (cityNorm.match(new RegExp(`\\b${normalized}`, 'i')) || 
                 asciiNorm.match(new RegExp(`\\b${normalized}`, 'i'))) {
          priority = 2;
        }
        // Contains in middle
        else if (cityNorm.includes(normalized) || asciiNorm.includes(normalized)) {
          priority = 3;
        }
        // Multi-term partial match
        else if (terms.length > 1) {
          const allTermsMatch = terms.every(t => 
            cityNorm.includes(t) || asciiNorm.includes(t)
          );
          if (allTermsMatch) priority = 4;
        }

        return {
          option,
          priority,
          labelLen: option.label.length,
          cityLen: cityName.length,
        };
      })
      .filter((item): item is { option: CityOption; priority: number; labelLen: number; cityLen: number } => Boolean(item))
      .filter(({ option }) => {
        // Deduplicate by exact coordinates only
        // This allows cities with same name in different states to appear (e.g., Rajkot in Gujarat vs Rajasthan)
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        
        return true;
      });

    // Sort by priority, then by city name length (shorter names first), then alphabetically
    ranked.sort(
      (a, b) =>
        a.priority - b.priority ||
        a.cityLen - b.cityLen ||
        a.option.label.localeCompare(b.option.label)
    );

    // Return top 12 results for dropdown (focused, relevant results)
    return ranked.slice(0, 12).map(r => r.option);
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] india_cities search exception', err);
    return [];
  }
}

async function searchGlobalCities(q: string): Promise<CityOption[]> {
  const normalized = normalizeQuery(q);
  if (!normalized) return [];

  const terms = normalized.split(' ').filter(Boolean);
  if (!terms.length) return [];

  try {
    // Try full-text search first (fast, indexed)
    let { data, error } = await supabase
      .from('global_cities')
      .select('name, ascii_name, country_code, country_name, region, lat, lon, timezone, population, is_capital')
      .textSearch('search_tokens', normalized, { type: 'websearch' })
      .limit(100);
    
    if (DEBUG) console.log('[searchGlobalCities] Full-text search results:', data?.length, 'cities for query:', normalized);
    
    // Fallback to ILIKE prefix match if full-text returns nothing (handles partial typing)
    if (!error && (!data || data.length === 0)) {
      if (DEBUG) console.log('[searchGlobalCities] No full-text results, trying ILIKE prefix match');
      const ilikeFallback = await supabase
        .from('global_cities')
        .select('name, ascii_name, country_code, country_name, region, lat, lon, timezone, population, is_capital')
        .ilike('ascii_name', `${normalized}%`)
        .limit(50);
      
      if (ilikeFallback.data && ilikeFallback.data.length > 0) {
        data = ilikeFallback.data;
        error = null;
        if (DEBUG) console.log('[searchGlobalCities] ILIKE fallback found:', data?.length, 'cities');
      }
    }
    
    if (error || !data) {
      if (DEBUG) console.warn('[searchGlobalCities] Search failed:', error);
      return [];
    }

    type GlobalCityRow = {
      name: string | null;
      ascii_name: string | null;
      country_code: string | null;
      country_name: string | null;
      region: string | null;
      lat: number | null;
      lon: number | null;
      timezone: string | null;
      population: number | null;
      is_capital: boolean | null;
    };

    const rows: GlobalCityRow[] = data as GlobalCityRow[];
    const seen = new Set<string>();
    const cityCountryCombo = new Set<string>(); // Track unique city+country combinations

    const ranked = rows
      .map(row => {
        if (row?.lat == null || row?.lon == null) return null;

        // Use ascii_name for display
        const cityName = cleanCityName(row?.ascii_name, row?.name);
        if (!cityName) return null;

        const cityNorm = cityName.toLowerCase();
        const region = row?.region || undefined;
        const country = row?.country_name || 'Unknown';
        
        // Build label with region for disambiguation
        const labelParts = region 
          ? [cityName, region, country]
          : [cityName, country];

        const option = hydrateOption({
          label: labelParts.join(', '),
          city: cityName,
          region: region,
          country: country,
          lat: Number(row.lat),
          lon: Number(row.lon),
          tz: row?.timezone || 'UTC',
          source: 'db',
        });

        // Improved priority scoring (capitals and populous cities rank higher)
        let priority = 10; // Default low priority
        
        // Boost capitals
        if (row.is_capital) priority -= 2;
        
        // Boost by population (higher population = lower priority number)
        if (row.population && row.population > 5000000) priority -= 2;
        else if (row.population && row.population > 1000000) priority -= 1;
        
        // Exact match (highest priority)
        if (cityNorm === normalized) {
          priority = 0;
        }
        // Starts with query (very high priority)
        else if (cityNorm.startsWith(normalized)) {
          priority = Math.max(0, priority - 8);
        }
        // Word boundary match (e.g., "new" matches "New York")
        else if (cityNorm.match(new RegExp(`\\b${normalized}`, 'i'))) {
          priority = Math.max(0, priority - 6);
        }
        // Contains in middle
        else if (cityNorm.includes(normalized)) {
          priority = Math.max(0, priority - 4);
        }
        // Multi-term partial match
        else if (terms.length > 1) {
          const allTermsMatch = terms.every(t => 
            cityNorm.includes(t) || 
            (region?.toLowerCase() || '').includes(t) ||
            (country?.toLowerCase() || '').includes(t)
          );
          if (allTermsMatch) priority = Math.max(0, priority - 3);
        }

        return {
          option,
          priority,
          labelLen: option.label.length,
          cityLen: cityName.length,
          population: row.population || 0,
        };
      })
      .filter((item): item is { option: CityOption; priority: number; labelLen: number; cityLen: number; population: number } => Boolean(item))
      .filter(({ option }) => {
        // Deduplicate by exact coordinates
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        
        // Also deduplicate by city+country combination
        const comboKey = `${option.city?.toLowerCase()}|${option.country?.toLowerCase() || 'none'}`;
        if (cityCountryCombo.has(comboKey)) return false;
        cityCountryCombo.add(comboKey);
        
        return true;
      });

    // Sort by priority, then by population (larger cities first), then by city name length
    ranked.sort(
      (a, b) =>
        a.priority - b.priority ||
        b.population - a.population ||
        a.cityLen - b.cityLen ||
        a.option.label.localeCompare(b.option.label)
    );

    // Return top 12 results for dropdown (focused, relevant results)
    return ranked.slice(0, 12).map(r => r.option);
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] global_cities search exception', err);
    return [];
  }
}




/** Fallback via free Nominatim mirror (no guaranteed TZ; we default to UTC) */
async function searchNominatim(q: string): Promise<CityOption[]> {
  const url = `https://geocode.maps.co/search?format=json&q=${encodeURIComponent(q)}&limit=10`;
  try {
    const r = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!r.ok) return [];
    const arr = await r.json();
    const out: CityOption[] = [];
    for (const it of arr) {
      const label: string = (it?.display_name || '') as string;
      const lat = safeNumber(it?.lat);
      const lon = safeNumber(it?.lon);
      if (lat == null || lon == null) continue;
      const tz =
        await resolveTimezoneForCoords(lat, lon) ??
        ((lat >= INDIA_LAT_MIN && lat <= INDIA_LAT_MAX && lon >= INDIA_LON_MIN && lon <= INDIA_LON_MAX)
          ? 'Asia/Kolkata'
          : null);
      if (!tz) {
        if (DEBUG) console.warn('[cityUtils] Nominatim result dropped (no timezone)', { label, lat, lon });
        continue;
      }

      out.push(
        hydrateOption({
          label: label || `${lat.toFixed(3)}, ${lon.toFixed(3)}`,
          lat,
          lon,
          tz,
          source: 'nominatim',
        }),
      );

    }
    return out;
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] Nominatim failed', err);
    return [];
  }
}

/* ========= Local cache (mem + localStorage) ========= */
type CachedQuery = {
  k: string;
  t: number;
  r: CityOption[];
};

const CACHE_NS = 'cityCache.v1';
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const CACHE_MAX = 300; // max distinct queries persisted

function normalizeQuery(q: string) {
  return q.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 120);
}

const memCache = new Map<string, CachedQuery>();

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function loadDiskCache(): CachedQuery[] {
  if (!hasWindow()) return [];
  try {
    const raw = window.localStorage.getItem(CACHE_NS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CachedQuery[];
    
    // Check if cache version changed - clear if mismatch
    const versionKey = `${CACHE_NS}_version`;
    const storedVersion = window.localStorage.getItem(versionKey);
    if (storedVersion !== CACHE_VERSION) {
      console.log(`[cityUtils] Cache version changed from ${storedVersion} to ${CACHE_VERSION}, clearing cache`);
      window.localStorage.removeItem(CACHE_NS);
      window.localStorage.setItem(versionKey, CACHE_VERSION);
      return [];
    }
    
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveDiskCache(items: CachedQuery[]) {
  if (!hasWindow()) return;
  try {
    const trimmed = items
      .sort((a, b) => b.t - a.t)
      .slice(0, CACHE_MAX);
    window.localStorage.setItem(CACHE_NS, JSON.stringify(trimmed));
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] saveDiskCache error', err);
  }
}

function getFromCache(q: string): CityOption[] | null {
  const key = normalizeQuery(q);
  const now = Date.now();

  const mem = memCache.get(key);
  if (mem && now - mem.t < CACHE_TTL_MS) return mem.r;

  const disk = loadDiskCache();
  const hit = disk.find(x => x.k === key && now - x.t < CACHE_TTL_MS);
  if (hit) {
    memCache.set(key, hit);
    return hit.r;
  }

  return null;
}

function putInCache(q: string, results: CityOption[]) {
  const key = normalizeQuery(q);
  const now = Date.now();
  const entry: CachedQuery = { k: key, t: now, r: results };

  memCache.set(key, entry);
  if (!hasWindow()) return;

  const disk = loadDiskCache().filter(x => x.k !== key);
  disk.unshift(entry);
  saveDiskCache(disk);
}

/* ========= Supabase DB cache (READ FIRST, WRITE ONLY ON SELECTION) ========= */
const DB_TABLE = 'city_cache';

type DBRow = {
  label: string;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  lat: number;
  lon: number;
  tz: string;
  source?: string | null;
  updated_at?: string | null;
};

type IndiaCityRow = {
  name: string | null;
  ascii_name: string | null;
  state: string | null;
  country: string | null;
  lat: number | null;
  lon: number | null;
  timezone: string | null;
};

function fromRow(row: DBRow): CityOption | null {
  const parts = (row.label || '').split(',').map(p => p.trim());
  const fallbackCity = parts[0];
  const fallbackState = parts[1];
  const fallbackCountry = parts[2];

  const cityName = cleanCityName(row.city, fallbackCity);
  if (!cityName) return null;

  const state = cleanStateName(row.region) ?? cleanStateName(fallbackState);
  const country = row.country || fallbackCountry || 'India';
  const label = [cityName, state, country].filter(Boolean).join(', ');

  return hydrateOption({
    label,
    city: cityName,
    region: state || undefined,
    country,
    lat: row.lat,
    lon: row.lon,
    tz: row.tz,
    source: 'db',
  });
}

async function dbSearchCities(q: string): Promise<CityOption[]> {
  const qNorm = normalizeQuery(q);
  try {
    const { data, error } = await supabase
      .from(DB_TABLE)
      .select('label, city, region, country, lat, lon, tz, source, updated_at')
      .ilike('label', `%${qNorm}%`)
      .order('updated_at', { ascending: false })
      .limit(10);

    if (error || !data) {
      if (DEBUG && error) console.warn('[cityUtils] dbSearchCities error', error);
      return [];
    }

    const mapped = (data as DBRow[])
  .map(fromRow)
  .filter((opt): opt is CityOption => Boolean(opt));
    const seen = new Set<string>();
    return mapped.filter(opt => {
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] dbSearchCities exception', err);
    return [];
  }
}

async function dbUpsertCitySelected(opt: CityOption): Promise<void> {
  const qNorm = normalizeQuery(opt.label);
  try {
    const payload = {
      label: opt.label,
      city: opt.city ?? null,
      region: opt.region ?? null,
      country: opt.country ?? null,
      lat: opt.lat,
      lon: opt.lon,
      tz: opt.tz,
      source: opt.source,
      q_norm: qNorm,
    };

    const up = await supabase
      .from(DB_TABLE)
      .upsert(payload, { onConflict: 'label,lat,lon,tz' })
      .select('label')
      .single();

    if (up.error) throw up.error;
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] dbUpsertCitySelected error', err);
  }
}

/* ========= MAIN SEARCH ========= */
export async function searchCitiesSmart(query: string): Promise<CityOption[]> {
  const q = (query || '').trim();

  if (q.length < 2) return POPULAR_CITY_OPTIONS.slice(0, 8);

  // Skip cache for debugging - check if results work without cache
  // const cached = getFromCache(q);
  // if (cached && cached.length) {
  //   if (DEBUG) console.log('[cityUtils] mem/disk cache hit');
  //   return cached;
  // }

  // IMPROVED: Search BOTH India + Global tables in parallel for ALL queries
  // This ensures international cities always appear regardless of query length
  // Previous logic was too restrictive (only searched global if India returned nothing)
  const [indiaHits, globalHits] = await Promise.all([
    searchIndiaCities(q),
    searchGlobalCities(q)
  ]);
  
  if (indiaHits.length || globalHits.length) {
    // Combine results - prioritize based on query length and match quality
    // Short queries (≤6 chars): Global first (e.g., "dubai" -> Dubai, UAE before local results)
    // Long queries (>6 chars): India first (e.g., "thiruvananthapuram" -> likely Indian city)
    const combined = q.length <= 6 
      ? [...globalHits, ...indiaHits]
      : [...indiaHits, ...globalHits];
    
    if (DEBUG) console.log('[cityUtils] Combined search - India:', indiaHits.length, 'Global:', globalHits.length);
    putInCache(q, combined);
    return combined;
  }

  // Priority 3: city_cache (user's saved cities - may have outdated data)
  const dbHit = await dbSearchCities(q);
  if (dbHit.length) {
    if (DEBUG) console.log('[cityUtils] city_cache hit:', dbHit.length);
    putInCache(q, dbHit);
    return dbHit;
  }

  // Priority 4: External APIs (OpenCage via edge function)
  const baseEdgeParams: OCParamMap = {
    limit: 12,
    language: 'en',
    autocomplete: 1,
    min_confidence: 2,
    types: OC_TYPES,
  };

  const callEdge = async (params: OCParamMap): Promise<CityOption[]> => {
    const payloadParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined)
    );
    try {
      const { data, error } = await supabase.functions.invoke('opencage', {
        body: {
          q,
          params: payloadParams,
        },
      });
      if (error) {
        if (DEBUG) console.warn('[cityUtils] OpenCage edge function error:', error);
        return [];
      }
      if (!data) {
        if (DEBUG) console.warn('[cityUtils] OpenCage edge function returned no data');
        return [];
      }
      const ocJson = Array.isArray((data as any).results)
        ? data
        : ((data as any)?.data || data);
      const results = mapOpenCage(ocJson, 'edge');
      if (DEBUG) console.log('[cityUtils] OpenCage edge function returned:', results.length, 'cities');
      return results;
    } catch (err) {
      if (DEBUG) console.warn('[cityUtils] OpenCage edge function exception:', err);
      return [];
    }
  };

  let edgeResults = await callEdge({ ...baseEdgeParams, countrycode: COUNTRY_PRIORITY_CODES });
  if (edgeResults.length) {
    putInCache(q, edgeResults);
    return edgeResults;
  }

  edgeResults = await callEdge({ ...baseEdgeParams, min_confidence: 1 });
  if (edgeResults.length) {
    putInCache(q, edgeResults);
    return edgeResults;
  }

  if (OC_KEY) {
    if (DEBUG) console.log('[cityUtils] Trying direct OpenCage API (key available)');
    const fetchDirect = async (countryCodes?: string, minConfidence: string = '1'): Promise<CityOption[]> => {
      try {
        const params = new URLSearchParams({
          key: OC_KEY,
          q,
          limit: '12',
          language: 'en',
          no_annotations: '0',
          autocomplete: '1',
          min_confidence: minConfidence,
          types: OC_TYPES,
          pretty: '0',
        });
        if (countryCodes) params.set('countrycode', countryCodes);
        const url = `https://api.opencagedata.com/geocode/v1/json?${params.toString()}`;
        if (DEBUG) console.log('[cityUtils] OpenCage request URL:', url.substring(0, 150) + '...');
        const r = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!r.ok) {
          if (DEBUG) console.warn('[cityUtils] Direct OpenCage API failed with status:', r.status);
          return [];
        }
        const json = await r.json();
        if (DEBUG) console.log('[cityUtils] OpenCage raw response:', json);
        const results = mapOpenCage(json, 'opencage');
        if (DEBUG) console.log('[cityUtils] Direct OpenCage API returned:', results.length, 'cities');
        return results;
      } catch (err) {
        if (DEBUG) console.warn('[cityUtils] Direct OpenCage API exception:', err);
        return [];
      }
    };

    let directResults = await fetchDirect(COUNTRY_PRIORITY_CODES);
    if (directResults.length) {
      putInCache(q, directResults);
      return directResults;
    }

    directResults = await fetchDirect(undefined, '1');
    if (directResults.length) {
      putInCache(q, directResults);
      return directResults;
    }
  } else {
    if (DEBUG) console.warn('[cityUtils] OpenCage direct API skipped - no OC_KEY available');
  }

  const nomi = await searchNominatim(q);
  if (nomi.length) {
    putInCache(q, nomi);
    return nomi;
  }

  return POPULAR_CITY_OPTIONS.filter(c => c.label.toLowerCase().includes(q.toLowerCase())).slice(0, 10);
}

export const searchCityOptions = searchCitiesSmart;

export function toUTC(opts: { year: number; month: number; day: number; hour: number; minute: number; second: number; tz: string }): string | null {
  try {
    const L = (window as any).luxon || (globalThis as any).luxon || null;
    const DateTime = L?.DateTime ?? (null as any);
    if (!DateTime) return null;
    const dt = DateTime.fromObject(
      { year: opts.year, month: opts.month, day: opts.day, hour: opts.hour, minute: opts.minute, second: opts.second },
      { zone: opts.tz }
    );
    return dt?.isValid ? dt.toUTC().toISO() : null;
  } catch {
    return null;
  }
}

export async function rememberCitySelection(chosen: CityOption, typed?: string) {
  try {
    const q = typed && typed.trim() ? typed : chosen.label;
    putInCache(q, [chosen]);
    await dbUpsertCitySelected(chosen);
  } catch (err) {
    if (DEBUG) console.warn('[cityUtils] rememberCitySelection error', err);
  }
}



